import {
  BarChart,
  Bar,
  Rectangle,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import AngledAxisTick from '../AngledAxisTick';
import AnalyticsEmptyState from "../../dashboard/AnalyticsEmptyState";
import { CHART_MARK_STROKE, CHART_MARK_STROKE_WIDTH } from '../../../helpers/chartPalette';

const OTHER_KEY = 'Anders, namelijk..';

const MultipleMomentBarChart = ({ data = [], totalResponses, source, graphColor }) => {
  if (!data || data.length === 0) {
    return <AnalyticsEmptyState />;
  }

  // Transform data to fit the Recharts format (carry __others for tooltip)
  const transformedData = data.map((item) => {
    const transformedResponses = item.responses.reduce((acc, response) => {
      acc[response.name] = response.count; // Use response name as key, count as value
      return acc;
    }, {});
    return {
      name: item.moment,
      __others: item.others || [], // ⬅️ attach others for this meetmoment
      ...transformedResponses,
    };
  });

  // Extract unique keys and colors for the bars (assumes same set across moments)
  const keysWithColors =
    data.length > 0
      ? data[0].responses.map((response) => ({
          key: response.name,
          color: response.color,
        }))
      : [];

  // Custom tooltip that lists "Anders, namelijk.." values
  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;

    const row = payload[0]?.payload || {};
    const others = Array.isArray(row.__others) ? row.__others : [];
    const showOthers = payload.some((p) => p.dataKey === OTHER_KEY);

    return (
      <div className="custom-tooltip">
        <p className="label">{`Meetmoment: ${label}`}</p>

        {payload.map((entry, idx) => (
          <p key={`tooltip-${idx}`} style={{ color: entry.color, margin: 0 }}>
            {`${entry.dataKey}: ${entry.value}`}
          </p>
        ))}

        {showOthers && others.length > 0 && (
          <div style={{ marginTop: 8 }}>
            <ul style={{ paddingLeft: 16, margin: '4px 0' }}>
              {others.slice(0, 5).map((txt, i) => (
                <li key={`other-${i}`}>{String(txt)}</li>
              ))}
            </ul>
            {others.length > 5 && (
              <div style={{ fontStyle: 'italic' }}>
                +{others.length - 5} meer…
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <ResponsiveContainer width="100%" height="100%" minHeight={250}>
        <BarChart
          data={transformedData}
          margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
        >
          <XAxis dataKey="name" axisLine={false} tickLine={false} stroke={graphColor || 'black'} tick={<AngledAxisTick fill={graphColor || undefined} />} interval={0} height={70}/>
          <YAxis allowDecimals={false} axisLine={false} tickLine={false} stroke={graphColor || 'black'} tick={{ fill: graphColor || undefined }}/>
          <Tooltip content={<CustomTooltip />} />
          <Legend wrapperStyle={{ color: graphColor || undefined }} />

          {/* Dynamically render bars with their respective colors */}
          {keysWithColors.map(({ key, color }) => (
            <Bar
              key={`bar-${key}`}
              dataKey={key}
              fill={color}
              stroke={CHART_MARK_STROKE}
              strokeWidth={CHART_MARK_STROKE_WIDTH}
              activeBar={<Rectangle stroke="darkblue" />} // keep your active look
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </>
  );
};

export default MultipleMomentBarChart;
